/**
 * Custom-icons store — per-user SVG overrides for wheel/renderer icons.
 *
 * Storage: IndexedDB object store 'custom-icons' inside the existing
 * 'coachplaybook-db'. Keyed by "sportId:actionId" (e.g. "basketball:dribble").
 * Value shape: { key, svg, updatedAt }
 *
 * Security: every incoming SVG runs through DOMPurify in SVG profile before
 * storage AND again before render. Uploaded SVGs are an XSS vector — script
 * blocks, event handlers, foreign objects, xlink:href with javascript URLs
 * are all removed by DOMPurify.
 */
import DOMPurify from 'dompurify';

const DB_NAME = 'coachplaybook-db';
const DB_VERSION = 4;               // bump v3 -> v4 to add custom-icons store
const STORE = 'custom-icons';

// DOMPurify config restricted to SVG shapes safe for icon use.
const PURIFY_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: false },
  FORBID_TAGS: ['script', 'foreignObject', 'iframe', 'object', 'embed', 'style'],
  FORBID_ATTR: [
    'onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur',
    'onchange', 'onsubmit', 'onreset', 'formaction', 'href', 'xlink:href',
  ],
  KEEP_CONTENT: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
};

/** Sanitize an SVG string. Returns cleaned SVG (may be empty if input was garbage). */
export function sanitizeSvg(raw) {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // DOMPurify.sanitize with SVG profile returns the cleaned markup.
  const cleaned = DOMPurify.sanitize(trimmed, PURIFY_CONFIG);
  // Guard against non-<svg> payloads sneaking through (extremely rare with
  // SVG profile but cheap to double-check).
  if (!/^<svg\b/i.test(cleaned)) return '';
  return cleaned;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
      // Preserve any pre-existing stores from earlier migrations.
      if (!db.objectStoreNames.contains('plays')) {
        db.createObjectStore('plays', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const buildKey = (sportId, actionId) => `${sportId}:${actionId}`;

/** Load one icon by (sportId, actionId). Returns { svg, updatedAt } | null. */
export async function getCustomIcon(sportId, actionId) {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const g = tx.objectStore(STORE).get(buildKey(sportId, actionId));
      g.onsuccess = () => resolve(g.result ? { svg: g.result.svg, updatedAt: g.result.updatedAt } : null);
      g.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Load ALL custom icons. Returns { "sport:action": svg } map. */
export async function getAllCustomIcons() {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const g = tx.objectStore(STORE).getAll();
      g.onsuccess = () => {
        const out = {};
        for (const row of (g.result || [])) out[row.key] = row.svg;
        resolve(out);
      };
      g.onerror = () => resolve({});
    });
  } catch {
    return {};
  }
}

/** Save a custom icon after sanitizing. Returns the stored SVG string. */
export async function setCustomIcon(sportId, actionId, rawSvg) {
  const svg = sanitizeSvg(rawSvg);
  if (!svg) throw new Error('SVG failed sanitization (empty or unsafe input)');
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({
      key: buildKey(sportId, actionId),
      svg,
      updatedAt: new Date().toISOString(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return svg;
}

/** Remove a custom override so the icon falls back to bundled SVG / emoji. */
export async function deleteCustomIcon(sportId, actionId) {
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(buildKey(sportId, actionId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch { /* ignore */ }
}

// Simple in-memory cache so wheel renders don't hit IDB on every open.
// Invalidated on every set/delete via bumpCacheVersion().
let cache = null;
let cacheVersion = 0;
export function bumpCacheVersion() { cacheVersion += 1; cache = null; }

export async function getCustomIconsCached() {
  if (cache) return cache;
  cache = await getAllCustomIcons();
  return cache;
}

export function getCacheVersion() { return cacheVersion; }
